import type { ClientOrganizationProfileRoleEnum } from '@DigitaleDoerfer/digitale-doerfer-api';

import { useGetOwnOrganizations } from '@/features/api/hooks/grapevine-organization/useGetOwnOrganizations';

export type OwnOrganizationRoleResult = {
  /**
   * The caller's role (`ADMIN | FUNKER`) in the given organization, or
   * `undefined` if the caller is not a member or the data has not loaded yet.
   */
  role: ClientOrganizationProfileRoleEnum | undefined;
  /** The underlying `myOrganizations` request is still in flight. */
  isLoading: boolean;
  /** The underlying `myOrganizations` request failed. */
  isError: boolean;
};

/**
 * Returns the caller's role (`ADMIN | FUNKER`) in the given organization along
 * with the loading / error state of the underlying request.
 *
 * Sourced from `myOrganizations` (which only lists orgs the caller has a role
 * in), not from the `getOrganizationById` detail endpoint — that response
 * does not include a caller-scoped role.
 */
export const useOwnOrganizationRole = (
  organizationId: string
): OwnOrganizationRoleResult => {
  const {
    data: ownOrganizations,
    isLoading,
    isError
  } = useGetOwnOrganizations();

  const role = ownOrganizations?.find(
    (organization) => organization.id === organizationId
  )?.role;

  return { role, isLoading, isError };
};
